import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { usePlusHomePageLastVisitDate } from "scmp-sdk";

import { article } from "shared/data";
import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { isString, notEmpty } from "shared/lib/utils";

import { AppFooter } from "scmp-app/components/app-footer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { plusPopupAtom } from "scmp-app/components/plus/atoms";
import { PlusBlurbPopupChecker } from "scmp-app/components/plus/blurb-popup";
import { usePlusBlurbPopup } from "scmp-app/components/plus/blurb-popup/hooks";
import { FeaturedOpinionBlock } from "scmp-app/components/plus/home/featured-opinion-block";
import { HighlightBlock } from "scmp-app/components/plus/home/highlight-block";
import { PlusMobileScreen } from "scmp-app/components/plus/mobile-screen";
import { PlusHomeSeo } from "scmp-app/components/plus/plus-home-seo";
import { PlusWidgetTheme } from "scmp-app/components/plus/plus-widget";
import { PlusWelcomePopup } from "scmp-app/components/plus/welcome-popup";
import { trackingAtom } from "scmp-app/components/tracking";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { PlusNonSubBlurbImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import { useGetPageLocationForPlusPage } from "scmp-app/pages/plus/hooks";
import type { plusPageQuery } from "scmp-app/queries/__generated__/plusPageQuery.graphql";

import {
  Container,
  FeaturedOpinion,
  FirstRow,
  HighlightFromSCMPPlus,
  PlusBlurbMessageWrapper,
  plusGlobalCss,
  SecondRow,
  StyledAgendaBlock,
  StyledDailyPulseBlock,
  StyledPlusBlurbMessage,
  Wrapper,
} from "./styles";

export const query = graphql`
  query plusPageQuery(
    $dailyPulseTypeUuid: String!
    $highlightQueueName: String!
    $nodeId: ID!
    $hasNodeId: Boolean!
  ) {
    node(id: $nodeId) @include(if: $hasNodeId) {
      ...plusHomeSeoNode
    }
    ...dailyPulseBlockQuery @arguments(dailyPulseTypeUuid: $dailyPulseTypeUuid)
    ...highlightBlockQuery @arguments(queueName: $highlightQueueName)
    ...featuredOpinionBlockQuery @arguments(excludeQueueName: $highlightQueueName)
  }
`;

const PlusPage: RelayComponent<PageProps, plusPageQuery> = ({ preloadedQuery }) => {
  const queryReference = usePreloadedQuery(query, preloadedQuery);
  const pageLocation = useGetPageLocationForPlusPage();
  const {
    hasScmpPlusAccessRight,
    hasScmpPlusUpgradeOptions,
    isEligibleForSCMPPlusFreeTrialSubscribeOffer,
  } = usePlusBlurbPopup();
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { isShowArticlePopup } = useAtomValue(plusPopupAtom);
  const isRosettaDataReady = asyncRosettaState?.result?.hasPianoExperienceExecuted ?? false;

  const impressionGA4Event = useCallback<() => PlusNonSubBlurbImpressionEvent>(
    () => ({
      action: "imp",
      category: "subs",
      customized_parameters: {
        call_to_action: hasScmpPlusUpgradeOptions
          ? "upgrade"
          : isEligibleForSCMPPlusFreeTrialSubscribeOffer
            ? "plus trial"
            : "subscribe",
        trigger_point: "non sub plus homepage blurb",
      },
      subcategory: "entry_point",
    }),
    [hasScmpPlusUpgradeOptions, isEligibleForSCMPPlusFreeTrialSubscribeOffer],
  );

  const ctaButtonOnClickEvent = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        call_to_action: hasScmpPlusUpgradeOptions
          ? "upgrade"
          : isEligibleForSCMPPlusFreeTrialSubscribeOffer
            ? "plus trial"
            : "subscribe",
        trigger_point: "non sub plus homepage blurb",
      },
      subcategory: "entry_point",
    });
  }, [hasScmpPlusUpgradeOptions, isEligibleForSCMPPlusFreeTrialSubscribeOffer]);

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: impressionGA4Event,
    options: {
      isSendGA4Tracking: true,
      shouldDisable: isShowArticlePopup, // should not send GA4 impression event when the article popup is showing in the first loading, as the blurb message will be hidden by the article popup
      shouldSendOnce: true,
    },
  });

  const router = useRouter();
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    if (!notEmpty(pageLocation)) {
      return;
    }
    const articleId = router.query.articleId;
    trackPageView?.({
      articleID: articleId,
      entityId: articleId,
      page_location: pageLocation,
      product: "plus",
    });
    // article_completion_sys get the articleId from the page.articleID
    // so insert articleID through trackPageView function into dataLayer variable
    // but page_location also need to be inserted into the page params
    // if add both into the dependency array, it will trigger the useEffect
    // twice every page changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLocation, trackPageView]);

  const { track } = usePlusHomePageLastVisitDate();
  useEffect(() => {
    track({ hasScmpPlusAccessRight });
  }, [track, hasScmpPlusAccessRight]);

  return (
    <>
      <PlusHomeSeo reference={queryReference.node} />
      <Container>
        <PlusMobileScreen />
        <PlusWelcomePopup />
        {isRosettaDataReady && !hasScmpPlusAccessRight && (
          <PlusBlurbMessageWrapper ref={captureTrackImpressionEventTargetElement}>
            <StyledPlusBlurbMessage ctaButtonOnClick={ctaButtonOnClickEvent} />
          </PlusBlurbMessageWrapper>
        )}
        <Wrapper>
          <FirstRow>
            <PlusBlurbPopupChecker>
              <StyledDailyPulseBlock reference={queryReference} />
            </PlusBlurbPopupChecker>
            <PlusBlurbPopupChecker>
              <StyledAgendaBlock plusWidgetTheme={PlusWidgetTheme.DARK} />
            </PlusBlurbPopupChecker>
          </FirstRow>

          <SecondRow>
            <FeaturedOpinion>
              <PlusBlurbPopupChecker>
                <FeaturedOpinionBlock reference={queryReference} />
              </PlusBlurbPopupChecker>
            </FeaturedOpinion>

            <HighlightFromSCMPPlus>
              <PlusBlurbPopupChecker>
                <HighlightBlock reference={queryReference} />
              </PlusBlurbPopupChecker>
            </HighlightFromSCMPPlus>
          </SecondRow>
        </Wrapper>
      </Container>

      <AppFooter variant="plus-with-app-link-row" />
    </>
  );
};

PlusPage.displayName = "Plus";

const pageProps: PageProps = {
  animateKey: "plus-home",
  appBarConfiguration: {
    variant: "scmp/plus",
  },
  appInitializerConfiguration: {
    application: "plus",
  },
  extraGlobalCss: plusGlobalCss,
};

export default withRelay(PlusPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    return {
      dailyPulseTypeUuid: article.types.dailyPulseBriefing.entityUuid,
      hasNodeId: notEmpty(context.query?.nodeId),
      highlightQueueName: "scmp_plus_home_news",
      nodeId: isString(context.query.nodeId) ? (context.query?.nodeId ?? "") : "",
    };
  },
});
